import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';

export type ShellBadge = {
    value: string | number;
    description: string;
    /** Tone class of modern.css */
    tone: string;
};

/** Flat menu entry with resolved link, used for favorites, recent visits and the filter */
export type ShellNavEntry = {
    text: string;
    icon: string;
    to: string;
    /** Name of the menu group, empty for top-level entries */
    groupText: string;
    item: NavigationDrawerAccess.DrawerDataItem;
};

const STORAGE_PREFIX = 'vanilla.shell.nav.';
const MAX_RECENT = 4;
/** With this many menu entries or fewer the whole menu is visible at a glance, so recent pages would only repeat it */
const MIN_ENTRIES_FOR_RECENT = 9;

const toneOfColor = (color: string | undefined): string => {
    if (color === 'success') {
        return 'tone-good';
    }
    if (color === 'error') {
        return 'tone-bad';
    }
    if (color === 'warning') {
        return 'tone-warn';
    }
    return 'tone-info';
};

const resolve = <T>(value: T | (() => T)): T => {
    return typeof value === 'function' ? (value as () => T)() : value;
};

export const resolveLink = (item: NavigationDrawerAccess.DrawerDataItem): string | null => {
    return resolve(item.to);
};

/** Badge of a menu entry, only if it has a value (counts of 0 are hidden like before) */
export const resolveBadge = (item: NavigationDrawerAccess.DrawerDataItem): ShellBadge | null => {
    if (!item.badge) {
        return null;
    }
    const value = resolve(item.badge.value);
    if (!value) {
        return null;
    }
    const description = item.badge.description ? resolve(item.badge.description) : null;
    return {value, description: description ? String(description) : '', tone: toneOfColor(item.badge.color)};
};

/** Badge shown on a closed group or in the collapsed sidebar: the highest count of its entries */
export const groupBadge = (group: NavigationDrawerAccess.DrawerGroupItem): ShellBadge | null => {
    let best: ShellBadge | null = null;
    for (const child of group.items) {
        if (child.type !== 'item') {
            continue;
        }
        const badge = resolveBadge(child);
        if (badge && (!best || Number(badge.value) > Number(best.value))) {
            best = badge;
        }
    }
    return best;
};

export const flattenEntries = (items: NavigationDrawerAccess.DrawerItem[]): ShellNavEntry[] => {
    const result: ShellNavEntry[] = [];
    const add = (item: NavigationDrawerAccess.DrawerDataItem, groupText: string) => {
        const to = resolveLink(item);
        if (to) {
            result.push({text: item.text, icon: item.icon, to, groupText, item});
        }
    };
    for (const item of items) {
        if (item.type === 'group') {
            item.items.forEach((child) => child.type === 'item' && add(child, item.text));
        } else {
            add(item, '');
        }
    }
    return result;
};

const normalize = (text: string): string => text.toLocaleLowerCase('de').normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Entries whose name (or group name) contains the filter text */
export const filterEntries = (entries: ShellNavEntry[], query: string): ShellNavEntry[] => {
    const q = normalize(query.trim());
    if (!q) {
        return entries;
    }
    return entries.filter((entry) => normalize(entry.text).includes(q) || normalize(entry.groupText).includes(q));
};

/** The menu entry belonging to a route: longest link that is a prefix of the path ("/" only matches exactly) */
export const entryOfPath = (entries: ShellNavEntry[], path: string): ShellNavEntry | null => {
    let best: ShellNavEntry | null = null;
    for (const entry of entries) {
        const matches = entry.to === '/' ? path === '/' : path === entry.to || path.startsWith(`${entry.to}/`);
        if (matches && (!best || entry.to.length > best.to.length)) {
            best = entry;
        }
    }
    return best;
};

const read = (key: string): string[] => {
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + key);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((el) => typeof el === 'string') : [];
    } catch {
        return [];
    }
};

const write = (key: string, values: string[]) => {
    try {
        localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(values));
    } catch {
        // Storage not available (private mode): favorites only last for this session
    }
};

/** Favorites, recent visits and collapsed sections of the menu, per user and only in this browser */
export const ShellNavMemory = {
    favorites(userId: string): string[] {
        return read(`fav.${userId}`);
    },
    toggleFavorite(userId: string, to: string): string[] {
        const current = read(`fav.${userId}`);
        const next = current.includes(to) ? current.filter((el) => el !== to) : [...current, to];
        write(`fav.${userId}`, next);
        return next;
    },
    /** Stores the favorites in the given order */
    setFavorites(userId: string, favorites: string[]): string[] {
        write(`fav.${userId}`, favorites);
        return favorites;
    },
    recent(userId: string): string[] {
        return read(`recent.${userId}`);
    },
    addRecent(userId: string, to: string): string[] {
        const next = [to, ...read(`recent.${userId}`).filter((el) => el !== to)].slice(0, MAX_RECENT + 4);
        write(`recent.${userId}`, next);
        return next;
    },
    collapsedSections(userId: string): string[] {
        return read(`collapsed.${userId}`);
    },
    toggleCollapsedSection(userId: string, section: string): string[] {
        const current = read(`collapsed.${userId}`);
        const next = current.includes(section) ? current.filter((el) => el !== section) : [...current, section];
        write(`collapsed.${userId}`, next);
        return next;
    },
    MAX_RECENT,
    MIN_ENTRIES_FOR_RECENT,
};

/** Environment shown next to the logo; nothing for production */
export const environmentLabel = (apiPath: string | undefined): string => {
    const path = (apiPath || '').toLowerCase();
    if (path.includes('localhost') || path.includes('127.0.0.1')) {
        return 'lokal';
    }
    if (path.includes('testing')) {
        return 'testing';
    }
    if (path.includes('staging')) {
        return 'staging';
    }
    return '';
};
